import React from 'react';
import * as yup from 'yup';
import useAwaitedForm from '../useAwaitedForm';
import { getBusinessCurrency } from '../../helpers/businesses';
import useUser from '../global/useUser';
import CashDiscountFormRow from '../../components/payments/CashDiscountFormRow';
import useCashDiscount from '../payments/useCashDiscount';

const itemFormSchema = yup.object({
  price: yup.number().min(0).required('Price is required'),
  quantity: yup
    .number()
    .integer()
    .min(1, 'Quantity must be at least 1')
    .required('Quantity is required'),
});

export type ItemFormValues = yup.InferType<typeof itemFormSchema>;

interface ShowItemFormOptions {
  name: string;
  initialPrice: number;
  initialQuantity?: number;
  title?: string;
  submitButtonText?: string;
  /** Selected payment method — drives terminal vs online rate/min fee. */
  paymentMethod?: string | null;
  /** Sum of (price × qty) for OTHER cart lines — drives proportional fee split. */
  groupOtherLinesTotal?: number;
  /** Offers removal from the same place the line is edited. */
  onRemove?: () => void;
}

export function useItemFormModal() {
  const onAwaitForm = useAwaitedForm();
  const { user } = useUser();
  const { attachedInputSx } = useCashDiscount();

  return React.useCallback(
    ({
      name,
      initialPrice,
      initialQuantity = 1,
      title,
      submitButtonText = 'Add',
      paymentMethod,
      groupOtherLinesTotal,
      onRemove,
    }: ShowItemFormOptions): Promise<ItemFormValues | null> =>
      onAwaitForm<ItemFormValues>({
        allowUntouched: true,
        form: {
          title: title ?? name,
          truncateTitle: true,
          destructiveAction: onRemove && {
            label: 'Remove item',
            onClick: onRemove,
          },
          initialValues: { price: initialPrice, quantity: initialQuantity },
          validationSchema: itemFormSchema,
          submitButtonText,
          inputRows: values => {
            const priceValue = Number(values.price) || 0;
            return [
              [
                {
                  inputType: 'number',
                  name: 'price',
                  label: 'Price',
                  min: 0,
                  step: 0.01,
                  suffix: getBusinessCurrency(user?.business),
                  inputSx: priceValue > 0 ? attachedInputSx : undefined,
                },
              ],
              [
                {
                  inputType: 'node',
                  isVisible: !!attachedInputSx && priceValue > 0,
                  node: React.createElement(CashDiscountFormRow, {
                    price: {
                      type: 'FIXED',
                      amount: priceValue,
                    },
                    variant: 'attached',
                    paymentMethod,
                    quantity: Number(values.quantity) || 1,
                    groupOtherLinesTotal,
                  }),
                },
              ],
              [
                {
                  inputType: 'number',
                  name: 'quantity',
                  label: 'Quantity',
                  min: 1,
                  step: 1,
                },
              ],
            ];
          },
        },
      }),
    [onAwaitForm, user, attachedInputSx],
  );
}
